const skillLabelCollator = new Intl.Collator("en", {
  sensitivity: "base",
  numeric: true,
});

/**
 * ラベル文字列同士を自然順で比較する
 * - 大文字小文字は区別しない
 * - 数値を文字列ではなく数値として扱う
 */
export function compareSkillLabels(leftLabel, rightLabel) {
  const normalizedLeftLabel =
    typeof leftLabel === "string" ? leftLabel : String(leftLabel ?? "");

  const normalizedRightLabel =
    typeof rightLabel === "string" ? rightLabel : String(rightLabel ?? "");

  return skillLabelCollator.compare(
    normalizedLeftLabel,
    normalizedRightLabel
  );
}

/**
 * displayName を ABC順で比較する
 */
export function compareByDisplayName(leftItem, rightItem) {
  const leftDisplayName =
    typeof leftItem?.displayName === "string" ? leftItem.displayName : "";

  const rightDisplayName =
    typeof rightItem?.displayName === "string" ? rightItem.displayName : "";

  return compareSkillLabels(leftDisplayName, rightDisplayName);
}

/**
 * name を ABC順で比較する
 */
export function compareByName(leftItem, rightItem) {
  const leftName = typeof leftItem?.name === "string" ? leftItem.name : "";
  const rightName = typeof rightItem?.name === "string" ? rightItem.name : "";

  return compareSkillLabels(leftName, rightName);
}

/**
 * parentSkill.displayName を ABC順で比較する
 */
export function compareByParentSkillDisplayName(leftItem, rightItem) {
  const leftDisplayName =
    typeof leftItem?.parentSkill?.displayName === "string"
      ? leftItem.parentSkill.displayName
      : "";

  const rightDisplayName =
    typeof rightItem?.parentSkill?.displayName === "string"
      ? rightItem.parentSkill.displayName
      : "";

  return compareSkillLabels(leftDisplayName, rightDisplayName);
}

/**
 * 比較用に値を数値へ変換する
 * - 数値に変換できない値は 0 として扱う
 */
function convertToSortableNumber(value) {
  const convertedNumber = Number(value);
  return Number.isFinite(convertedNumber) ? convertedNumber : 0;
}

/**
 * 経験年数ラベルを月数に変換する
 * 例:
 * - "3年" => 36
 * - "1年6ヶ月" => 18
 * - "8ヶ月" => 8
 */
function convertExperienceLabelToMonths(experienceLabel) {
  if (typeof experienceLabel !== "string") {
    return 0;
  }

  const yearsMatchResult = experienceLabel.match(/(\d+)\s*年/);
  const monthsMatchResult = experienceLabel.match(/(\d+)\s*ヶ?月/);

  const years = yearsMatchResult ? Number(yearsMatchResult[1]) : 0;
  const months = monthsMatchResult ? Number(monthsMatchResult[1]) : 0;

  return years * 12 + months;
}

// framework 用
// 評価が高い順→ 年数が長い順 → ABC順
export function compareByLevelExperienceAndDisplayName(leftItem, rightItem) {
  const leftLevelValue = convertToSortableNumber(leftItem?.levelValue);
  const rightLevelValue = convertToSortableNumber(rightItem?.levelValue);

  if (leftLevelValue !== rightLevelValue) {
    return rightLevelValue - leftLevelValue;
  }

  const leftExperienceMonths = convertExperienceLabelToMonths(
    leftItem?.experienceLabel
  );
  const rightExperienceMonths = convertExperienceLabelToMonths(
    rightItem?.experienceLabel
  );

  if (leftExperienceMonths !== rightExperienceMonths) {
    return rightExperienceMonths - leftExperienceMonths;
  }

  return compareByDisplayName(leftItem, rightItem);
}

// Tool 用
// 年数が長い順 → ABC順
export function compareByExperienceAndDisplayName(leftItem, rightItem) {
  const leftExperienceMonths = convertExperienceLabelToMonths(
    leftItem?.parentSkill?.experienceLabel
  );
  const rightExperienceMonths = convertExperienceLabelToMonths(
    rightItem?.parentSkill?.experienceLabel
  );

  if (leftExperienceMonths !== rightExperienceMonths) {
    return rightExperienceMonths - leftExperienceMonths;
  }

  return compareByParentSkillDisplayName(leftItem, rightItem);
}

// skillGroup.parentSkill 用
// 評価が高い順 → 年数が長い順 → ABC順
export function compareByParentSkillLevelExperienceAndDisplayName(
  leftItem,
  rightItem
) {
  const leftLevelValue = convertToSortableNumber(
    leftItem?.parentSkill?.levelValue
  );
  const rightLevelValue = convertToSortableNumber(
    rightItem?.parentSkill?.levelValue
  );

  if (leftLevelValue !== rightLevelValue) {
    return rightLevelValue - leftLevelValue;
  }

  const leftExperienceMonths = convertExperienceLabelToMonths(
    leftItem?.parentSkill?.experienceLabel
  );
  const rightExperienceMonths = convertExperienceLabelToMonths(
    rightItem?.parentSkill?.experienceLabel
  );

  if (leftExperienceMonths !== rightExperienceMonths) {
    return rightExperienceMonths - leftExperienceMonths;
  }

  return compareByParentSkillDisplayName(leftItem, rightItem);
}